import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DateWindow } from "../api/tenantInternalReports";
import { OptionNameValuePair } from "./interfaces/types";

/**
 * cn Utility Function
 *
 * Purpose: Merges Tailwind CSS classes intelligently. It combines class names
 * using `clsx` (which allows conditional classes) and then uses `tailwind-merge`
 * to resolve conflicting Tailwind classes (e.g., merging `p-4` and `p-6` into just `p-6`).
 **/

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// Example Usage in a component:
// import { cn } from "@/lib/utils";
//
// const isActive = true;
// const className = cn(
//   "base-class",
//   "p-4", // This might get overridden by a prop
//   isActive ? "bg-primary" : "bg-secondary",
//   props.className // Allows overriding padding etc.
// );

export function getCurrentDate(): Date {
  const current_date = new Date();
  return current_date;
}

export function formatDateStringForDisplay(localDateStr: string): string {
  const [year, month, day] = localDateStr.split("-").map(Number);

  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatDateToLocalInputValue(
  dateValue?: string | Date | null,
): string {
  if (!dateValue) return "";

  try {
    const date =
      typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
}

export function toUTCDateString(localDateStr: string): string {
  const [year, month, day] = localDateStr.split("-").map(Number);

  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const seconds = now.getSeconds();
  const ms = now.getMilliseconds();

  const localDateTime = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds,
    ms,
  );

  const utcYear = localDateTime.getUTCFullYear();
  const utcMonth = String(localDateTime.getUTCMonth() + 1).padStart(2, "0");
  const utcDay = String(localDateTime.getUTCDate()).padStart(2, "0");

  return `${utcYear}-${utcMonth}-${utcDay}`;
}

export function addDaysToToday(days: number): Date {
  const today = getCurrentDate();
  today.setDate(today.getDate() + days);
  return today;
}

export function getFirstDayOfCurrentMonth(): Date {
  const now = getCurrentDate();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function generateSafeIdFromString(str: string): string {
  return str
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export const formatCurrency = (
  amount: number,
  currencyCode: string = "INR",
  locale: string = "en-IN",
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: minimumFractionDigits,
      maximumFractionDigits: maximumFractionDigits,
    }).format(amount);
  } catch (error) {
    console.error("Currency formatting error:", error);
    return ""; // Graceful fallback
  }
};
export const formatDateToDdMmYyyy = (
  date: Date | string | null | undefined,
): string => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "Invalid Date";
  return d.toLocaleDateString("en-GB");
};

export function toUtcDate(input?: string | Date | null): Date | null {
  const d = input ? new Date(input) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

export const truncatefileNameText = (text: string, maxLength: number = 15) => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
};

export function generateCaseUIN(prefix: string | undefined): string {
  if (!prefix) return "UIN";

  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");

  const hour = now.getHours();
  const minute = now.getMinutes();
  const second = now.getSeconds();

  const dateStr = `${y}${m}${d}`;

  const timeStamp = (hour * 3600 + minute * 60 + second)
    .toString(36)
    .toUpperCase()
    .padStart(4, "0");

  const randChar = Math.floor(Math.random() * 36)
    .toString(36)
    .toUpperCase();

  return `${prefix.trim().toUpperCase()}-${dateStr}-${timeStamp}${randChar}`;
}
export const formatCompactCurrency = (value: number): string => {
  let formatted: string;

  if (value >= 10000000) {
    formatted = `${(value / 10000000).toFixed(1)}Cr`;
  } else if (value >= 100000) {
    formatted = `${(value / 100000).toFixed(1)}L`;
  } else if (value >= 1000) {
    formatted = `${(value / 1000).toFixed(1)}K`;
  } else {
    formatted = `${value}`;
  }

  return `₹${formatted}`;
};

// Get initials from full name
export function getInitials(name: string): string {
  if (!name || name.trim().length === 0) return "";
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function toTitleCase(str: string) {
  return str
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
    );
}

export const formatIndianCompactNumber = (
  num: number,
  options: { includeCurrency?: boolean } = {},
): string => {
  const { includeCurrency = false } = options;

  let value: string;
  if (num >= 10000000000) value = `${(num / 10000000000).toFixed(1).replace(/\.0$/, "")}k Cr`;
  else if (num >= 10000000) value = `${(num / 10000000).toFixed(1).replace(/\.0$/, "")} Cr`;
  else if (num >= 100000) value = `${(num / 100000).toFixed(1).replace(/\.0$/, "")} L`;
  else if (num >= 1000) value = `${(num / 1000).toFixed(1).replace(/\.0$/, "")} K`;
  else value = String(num);

  return includeCurrency ? `₹${value}` : value;
};

/**
 * Normalizes user-entered numeric text by:
 * - removing commas and surrounding whitespace
 * - keeping digits 0–9
 * - allowing at most a single decimal point.
 */
export const normalizeNumericWithDecimal = (val?: string): string => {
  const input = (val ?? "").replace(/,/g, "").trim();
  if (!input) return "";

  let result = "";
  let hasDot = false;

  for (const ch of input) {
    if (ch >= "0" && ch <= "9") {
      result += ch;
    } else if (ch === "." && !hasDot) {
      result += ch;
      hasDot = true;
    }
  }

  return result;
};

/**
 * Formats a numeric string using Indian-style digit grouping
 * (e.g., 12,34,567) for the integer part while preserving the
 * fractional part exactly as entered.
 */
export const formatIndianInputNumeric = (numeric: string): string => {
  if (!numeric) return "";

  // Special case: user has typed just "."
  if (numeric === ".") return ".";

  const [rawIntPart, rawFracPart] = numeric.split(".");
  const hasDot = numeric.includes(".");

  const intPartSafe = rawIntPart === "" ? "0" : rawIntPart;
  const intNum = Number(intPartSafe);
  if (Number.isNaN(intNum)) return numeric;

  const groupedInt = intNum.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });

  if (hasDot) {
    if (rawFracPart !== undefined) {
      return `${groupedInt}.${rawFracPart}`;
    }
    return `${groupedInt}.`;
  }

  return groupedInt;
};

/**
 * Formats any numeric text (potentially containing commas) into
 * Indian-grouped text with the decimal part preserved.
 * Used for read-only tooltips (full value).
 */
export const formatIndianGroupedWithDecimals = (val?: string): string => {
  const numeric = normalizeNumericWithDecimal(val);
  if (!numeric) return "";
  if (numeric === ".") return ".";

  const [rawIntPart, rawFracPart] = numeric.split(".");
  const hasDot = numeric.includes(".");

  const groupedInt = formatIndianInputNumeric(rawIntPart === "" ? "0" : rawIntPart);

  if (hasDot) {
    if (rawFracPart !== undefined && rawFracPart.length > 0) {
      return `${groupedInt}.${rawFracPart}`;
    }
    return `${groupedInt}.0`;
  }

  return groupedInt;
};

export const LAKH = 100000;

export function formatDateWithOrdinal(
  dateInput?: string | Date | null
): string {
  if (!dateInput) return "";

  const date =
    typeof dateInput === "string"
      ? new Date(dateInput)
      : dateInput;

  if (isNaN(date.getTime())) return "";

  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", {
    month: "short",
  });
  const year = date.getFullYear();

  const getOrdinalSuffix = (n: number) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
}

export const sortOptions = (
    options: OptionNameValuePair<string>[]
  ): OptionNameValuePair<string>[] => {
    return [...options].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
  };

/* ------------------------------------------------------------------ *
 * Reporting period / financial-year helpers (single source of truth)
 * Shared by the report pages, the month/FY calendar pickers and the
 * report API mocks so the date math is never reimplemented per place.
 * ------------------------------------------------------------------ */

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

export const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const pad2 = (value: number): string => String(value).padStart(2, "0");

/** Canonical month key from a year + month index (0-11), e.g. (2026, 5) → "2026-06". */
export const monthKey = (year: number, monthIndex: number): string =>
  `${year}-${pad2(monthIndex + 1)}`;

/** Current month as a canonical period key, e.g. "2026-06". */
export const getCurrentMonthKey = (date = new Date()): string =>
  monthKey(date.getFullYear(), date.getMonth());

/** Parse a "2026-06" month key into its month index (0-11) and year. */
export const parseMonthLabel = (
  key: string
): { monthIndex: number; year: number } => {
  const [yearStr, monthStr] = key.split("-");
  return { monthIndex: Number(monthStr) - 1, year: Number(yearStr) };
};

/** Full month label for display, e.g. "2026-06" → "June 2026". */
export const formatMonthLabel = (key: string): string => {
  const { monthIndex, year } = parseMonthLabel(key);
  return monthIndex < 0 || monthIndex > 11 ? key : `${MONTH_NAMES[monthIndex]} ${year}`;
};

/** Compact month label for table headers, e.g. "2026-06" → "Jun 2026". */
export const shortMonthLabel = (key: string): string => {
  const { monthIndex, year } = parseMonthLabel(key);
  return monthIndex < 0 || monthIndex > 11 ? key : `${MONTH_ABBR[monthIndex]} ${year}`;
};

/** Starting calendar year of the Indian fiscal year (Apr–Mar) containing `date`. */
export const currentFyStartYear = (date = new Date()): number =>
  date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;

/** Canonical FY key for a starting year, e.g. 2026 → "2026-2027". */
export const fyKey = (startYear: number): string => `${startYear}-${startYear + 1}`;

/** Starting year parsed from an FY key, e.g. "2026-2027" → 2026. */
export const parseFyStartYear = (key: string): number | null => {
  const match = key.match(/^(\d{4})-\d{4}$/);
  return match ? Number(match[1]) : null;
};

/** FY label for display, e.g. "2026-2027" → "FY 2026-27". */
export const formatFyLabel = (key: string): string => {
  const start = parseFyStartYear(key);
  return start === null ? key : `FY ${start}-${String(start + 1).slice(-2)}`;
};

/** The FY immediately preceding the given one, e.g. "2026-2027" → "2025-2026". */
export const priorFyKey = (key: string): string => {
  const start = parseFyStartYear(key);
  return start === null ? key : fyKey(start - 1);
};

/** Current fiscal year as a canonical key, e.g. "2026-2027". */
export const getCurrentFyKey = (date = new Date()): string =>
  fyKey(currentFyStartYear(date));

/** Date as `YYYY-MM-DD` (the format used in request bodies across the app). */
const toApiDate = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** Last calendar day of the month for a "2026-06" key, as a Date. */
const monthEndDate = (key: string): Date => {
  const { monthIndex, year } = parseMonthLabel(key);
  return new Date(year, monthIndex + 1, 0);
};

/** The selected month as a closed window, e.g. "2026-06" → 2026-06-01..2026-06-30. */
export const monthLabelToDateWindow = (key: string): DateWindow => {
  const { monthIndex, year } = parseMonthLabel(key);
  return {
    start_date: toApiDate(new Date(year, monthIndex, 1)),
    end_date: toApiDate(new Date(year, monthIndex + 1, 0)),
  };
};

/** Life-to-date window up to the selected month end (open start → since inception). */
export const lifeToDateWindowForMonth = (key: string): DateWindow => ({
  end_date: toApiDate(monthEndDate(key)),
});

/** A fiscal-year key as a closed window, e.g. "2026-2027" → 2026-04-01..2027-03-31. */
export const fyToDateWindow = (key: string): DateWindow => {
  const start = parseFyStartYear(key) ?? currentFyStartYear();
  return {
    start_date: toApiDate(new Date(start, 3, 1)),
    end_date: toApiDate(new Date(start + 1, 2, 31)),
  };
};

/** A date as `DD-MM-YYYY` (the report meta-header display format). */
export const formatDisplayDate = (date: Date): string =>
  `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;

/** Compact `YYYYMMDD` stamp used in exported file names, e.g. "20260716". */
export const fileDateStamp = (date = new Date()): string =>
  `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;

/** Month end as a display date, e.g. "2026-06" → "30-06-2026". */
export const monthEndDisplayDate = (key: string): string =>
  formatDisplayDate(monthEndDate(key));


/** Save a Blob to disk via a temporary object URL + anchor click (Cases pattern). */
export const saveBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};